/* global ROOT */
export default {
    namespaced: true,

    state: () => ({
        data: {}, // see bridge._fetchCache()
        tvOptions: { // All the current TV selections made in the buyflow
            service: { // The base TV service selection Locals+ or null
                decision: null,
                selection: null,
            },
            equipment: { // The TV equipment selection (TiVo equipment or Stream TV equipment) or null
                decision: null,
                selection: null,
            },
            extraTVBoxes: { // Additional TV boxes selection (TiVo Mini, TiVo DVR, or Stream TV Boxes) or null
                decision: null,
                selection: null,
                quantity: 0,
            },
            channels: { // The channel lineup selection (Variety+, Family+, premium, international, or Streaming Services) or null
                decision: null,
                selection: [],
            },
            declined: false, // The TV service declined selection
        },

        channelLineup: { // The channel lineup data including all channels
            isVisible: false,
            allChannels: [],
        },
    }),

    getters: {
        getServiceType (state) {
            // returns the type of service available (tivoServices or iptvServices)
            return Object.keys(state.data?.tivoServices).length > 0 ? 'tivoServices' : 'iptvServices'
        },
        onlyStreaming (state) {
            // returns true if only streaming services are the only services available, otherwise false
            return !(Object.keys(state.data?.tivoServices).length > 0 || Object.keys(state.data?.iptvServices).length > 0)
        },
        getServicePrice: (state, getters, rootState, rootGetters) => (service) => {
            if (rootGetters['Buyflow/isOhio']) {
                return service.price +
                    state.data?.packages?.localsPlus?.price -
                    (
                        rootGetters['Buyflow/internet/selectedService'].name === 'Unlimited GigaFast' ?
                            (state.data?.packages?.localsPlus?.priceDifference + service.priceDifference) : 25
                    )
            }

            return service.price +
                   state.data?.packages?.localsPlus?.price -
                   state.data?.packages?.localsPlus?.priceDifference +
                   (state.data?.credits.length > 0 && state.data?.credits[0] ?state.data?.credits.reduce((acc, credit) => acc += credit.price, 0): 0) -
                   ( rootGetters['Buyflow/isOhio'] ? service.priceDifference : 0 )
        },
        hasSelectedAService (state) {
            // returns true if TV Services are selected, otherwise false
            return state.tvOptions.service.selection !== null
        },
        isServiceSelected: (state, getters) => service => {
            // returns true if TV Services and equipment are selected, otherwise false
            const selection = state.tvOptions.equipment.selection

            if (!getters.hasSelectedAService){
                return false
            }
            if (Array.isArray(selection)) {
                return selection.findIndex(s => s.name === service.name) > -1
            }
            return (selection?.name === service.name) ?? false
        },
        extraEquipmentsCount (state) {
            // returns the number of extra TV Boxes in the cart (TiVo Mini, TiVo DVR, or Stream TV Boxes)
            return state.tvOptions.extraTVBoxes.quantity ?? 0
        },
        isPackageSelected: (state) => pkg => {
            // returns true if the package is selected, otherwise false
            return state.tvOptions.channels.selection.findIndex(p => p.name === pkg.name) > -1
        },
        filteredChannels (state) {
            // returns an object of all the channels filtered by category
            return {
                localsPlus: state.channelLineup?.allChannels.filter(channel => channel.category === 'Locals+').sort((a, b) => a.channelNo - b.channelNo),
                varietyPlus: state.channelLineup?.allChannels.filter(channel => channel.category === 'Variety+').sort((a, b) => a.channelNo - b.channelNo),
                familyPlus: state.channelLineup?.allChannels.filter(channel => channel.category === 'Family+').sort((a, b) => a.channelNo - b.channelNo),
                addOns: state.channelLineup?.allChannels.filter(channel => channel.category === 'Add-Ons').sort((a, b) => a.channelNo - b.channelNo),
            }
        },
        hasDeclined(state) {
            // returns true if the TV service has been declined, otherwise false
            return state.tvOptions.declined
        },
        isHidden (state, getters, rootState, rootGetters) {
            // returns true if the TV section should be hidden, otherwise false
            return rootGetters['Buyflow/buyflowFeature']('buyflow_hide', 'tv')
        },
        isMinimal (state, getters, rootState, rootGetters) {
            // returns true if the TV section should have minimal ui, otherwise false
            return rootGetters['Buyflow/buyflowFeature']('buyflow_minimal', 'tv')
        },
    },

    mutations: {
        SET_CHANNEL_LINEUP_DATA (state, payload) {
            state.channelLineup.allChannels = payload
        },
        TOGGLE_CHANNEL_LINEUP (state, value = null) {
            state.channelLineup.isVisible = value === null ? !state.channelLineup.isVisible : value
        },
        UPDATE_TV_OPTIONS (state, decision) {
            state.tvOptions = {...state.tvOptions, ...decision }
        },
    },

    actions: {
        init({ commit, state, rootState}) {
            // This is called from the main buyflow store initialize() method to initialize the TV section selections
            const cartAddOns   = rootState.Buyflow.receipt.cartData?.addOns?.tv ?? []
            const cartServices = rootState.Buyflow.receipt.cartData?.services?.tv ?? []

            const equipmentList = cartServices.map(s => ({ 'Cloud DVR':'Cloud DVR - 150 hours', 'Cloud DVR Max':'Cloud DVR Max - 300 hours' }[s.name] || s.name))
            const channelList   = cartAddOns.packages?.map(p => ({ 'Cloud DVR':'Cloud DVR - 150 hours', 'Cloud DVR Max':'Cloud DVR Max - 300 hours' }[p.name] || p.name))
            const service       = cartAddOns?.base?.length? Object.values(state.data?.packages || {}).find(o => o.name === cartAddOns?.base[0]?.name): null
            const equipment     = Object.values({...state.data?.iptvServices,...state.data?.tivoServices }).filter(o => equipmentList?.indexOf(o.name) > -1)
            const extraTVBoxes  = cartAddOns?.equipment?.length? Object.values({...state.data?.iptvEquipments,...state.data?.tivoEquipments }).find(o => o.name === cartAddOns?.equipment[0]?.name): null
            const channels      = Object.values({ ...state.data?.packages, ...state.data?.streamingServices, ...state.data?.premiumChannels?.concat(state.data?.internationalChannels)}).filter(o => channelList?.indexOf(o.name) > -1)

            commit('UPDATE_TV_OPTIONS', {
                service: {
                    decision: service?.decision ?? null,
                    selection: service ?? null,
                },
                equipment: {
                    decision: equipment[0]?.decision ?? null,
                    selection: equipment ?? null,
                },
                extraTVBoxes: {
                    decision: extraTVBoxes?.decision ?? null,
                    selection: extraTVBoxes ?? null,
                    quantity: cartAddOns?.equipment?.length? cartAddOns?.equipment[0]?.quantity: 0,
                },
                channels: {
                    decision: channels[0]?.decision ?? null,
                    selection: channels ?? [],
                },
            })
        },
        selectTVOptions ({ dispatch, commit, state, getters, rootState, rootGetters }, payload) {
            // If the tv type is tivo and a selection is being made then we need to change the install type to pro install
            if (getters.getServiceType === 'tivoServices' && payload?.service?.selection && rootGetters['Buyflow/finalize/hasSelectedSelfInstall']) {
                dispatch('Buyflow/finalize/submitInstallationFulfillment', rootState.Buyflow.finalize.installData?.proInstall, ROOT)
            }

            // This call makes the TV selections and updates the tvOptions state
            commit('UPDATE_TV_OPTIONS', payload)
            return dispatch('Buyflow/updateDecisions', payload, ROOT)
        },
        selectChannels({ dispatch, commit, state, getters }, payload)  {
            // This call makes the TV channel selections and updates the tvOptions state
            const channels = state.tvOptions.channels.selection

            // Convert the payload to an array if it's not already
            if (!Array.isArray(payload)) {
                payload = [payload]
            }

            // for each channel in the payload, check if it's already selected and add/remove it from the selection
            payload.forEach(channel => {
                const index = channels.findIndex(c => c.name === channel.name)
                if (index > -1) {
                    channels.splice(index, 1)
                } else {
                    channels.push(channel)
                }
            })

            dispatch('selectTVOptions', { channels: { decision: payload[0].decision, selection: channels }})
        },
        declineService ({ dispatch, state, commit, getters, rootGetters, rootState }, value = false) {
            // This call declines the TV service and updates the tvOptions state
            if (value) {
                dispatch('selectTVOptions', {
                    service: {
                        decision: state.tvOptions.service.decision,
                        selection: null,
                    },
                    equipment: {
                        decision: state.tvOptions.equipment.decision,
                        selection: null,
                    },
                    extraTVBoxes: {
                        decision: state.tvOptions.extraTVBoxes.decision,
                        selection: null,
                        quantity: 0,
                    },
                    channels: {
                        decision: state.tvOptions.channels.decision,
                        selection: [],
                    },
                    declined: value,
                })
                // If the tv type is tivo and the service is being declined then we need to change the install type back to self install
                if (getters.getServiceType === 'tivoServices' && rootState.Buyflow.finalize.installData?.selfInstallKit  && !rootGetters['Buyflow/finalize/hasSelectedSelfInstall']) {
                    dispatch('Buyflow/finalize/submitInstallationFulfillment', rootState.Buyflow.finalize.installData?.selfInstallKit, ROOT)
                }
            }
        },
        fetchChannelLineup ({ state, commit, dispatch, getters, rootState }) {
            // If the channel lineup data has already been fetched don't re-fetch it
            if (state.channelLineup.allChannels.length > 0) {
                return Promise.resolve()
            }

            // Get meta channels data
            const metaChannels = rootState.meta.channels

            const type = getters['getServiceType'] === 'iptvServices' ? 'resi_ip' : 'resi'

            if (metaChannels && metaChannels[type]) {
                const channels = metaChannels[type]

                const cleanerData = channels.map(item => ({
                    channelName: item.name,           // channelName
                    channelNo: item.channelNumber,    // channelNumber
                    category: item.category,          // category
                    subCategory: item.subCategory,    // subCategory 
                    genre: item.genre                 // genre
                }))

                commit('SET_CHANNEL_LINEUP_DATA', cleanerData)
                
                return Promise.resolve(cleanerData)
            }
        },
        toggleChannelLineup ({ commit, dispatch }, value) {
            // This call toggles the channel lineup overlay and updates the openOverlays counter
            dispatch('adjustOpenOverlaysCounter', { value }, ROOT)
            commit('TOGGLE_CHANNEL_LINEUP', value)
        },
    },
}
